import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import { useApp, stampLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { AiScenario } from '../store/seed';
import { aiProviders } from './aiProviders';
const exampleModels = [
  'Mid-size model (example)',
  'Small model (example)',
  'Large model (example)',
];
const demoTestOutcomes: [AiScenario, string][] = [
  ['success', 'Works'],
  ['invalid', 'Invalid key'],
  ['billing', 'Billing needed'],
  ['model', 'Model unavailable'],
  ['temporary', 'Temporary failure'],
];
const PRICES_LAST_CHECKED_ON = new Date(2026, 10, 3);
const SIMULATED_KEY_TEST_DELAY_MS = 500;

export function AiConnectForm({
  onConnected,
  connectedByName,
  compact,
}: {
  onConnected?: () => void;
  connectedByName?: string;
  compact?: boolean;
}) {
  const { translate, language, readOnly } = useApp();
  const { route, actor, notify } = useUi();
  const { commands } = useDemo();
  const { aiConnection } = useWorkspaceData(route.workspaceId);
  const [provider, setProvider] = useState(aiConnection.provider ?? aiProviders[0].id);
  const [model, setModel] = useState(aiConnection.model ?? exampleModels[0]);
  const [key, setKey] = useState('');
  const [scenario, setScenario] = useState<AiScenario>('success');
  const [result, setResult] = useState<AiScenario | 'empty' | null>(null);
  const [busy, setBusy] = useState(false);
  const selectedProvider =
    aiProviders.find((providerOption) => providerOption.id === provider) ?? aiProviders[0];
  const pricesCheckedDate = new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(PRICES_LAST_CHECKED_ON);

  function testConnection() {
    if (!key.trim()) {
      setResult('empty');
      return;
    }
    setBusy(true);
    setResult(null);
    window.setTimeout(() => {
      const testOutcome = commands.testAi(
        route.workspaceId,
        { provider, model, key: key.trim(), scenario },
        connectedByName ?? actor.name
      );
      setBusy(false);
      setResult(testOutcome);
      if (testOutcome === 'success') {
        notify(
          translate('AI connected. AMY can draft with {provider} now.', {
            provider: selectedProvider.name,
          })
        );
        setKey('');
        onConnected?.();
      }
    }, SIMULATED_KEY_TEST_DELAY_MS);
  }

  function testResultMessage(outcome: AiScenario | 'empty') {
    switch (outcome) {
      case 'empty':
        return (
          <>
            <strong>{translate('Paste a key first')}</strong>
            <div>
              {translate('The key field is empty. Use the demo key, or paste the key you created.')}
            </div>
          </>
        );
      case 'success':
        return (
          <>
            <strong>{translate('Connected')}</strong>
            <div>
              {translate('{provider} · {model}. AMY drafts with this account from now on.', {
                provider: selectedProvider.name,
                model: translate(model),
              })}
            </div>
          </>
        );
      case 'invalid':
        return (
          <>
            <strong>
              {translate('{provider} did not accept this key', { provider: selectedProvider.name })}
            </strong>
            <div>
              {translate(
                'It may be incomplete or deleted. Create a new key named AMY, copy it in full, and test again.'
              )}
            </div>
            <a
              className="button small"
              href={selectedProvider.apiKeysPageUrl}
              target="_blank"
              rel="noreferrer"
            >
              <SafeIcon name="FiExternalLink" size={14} />
              {translate('Create a new key')}
            </a>
          </>
        );
      case 'billing':
        return (
          <>
            <strong>{translate('The key works, but billing is not set up')}</strong>
            <div>
              {translate(
                'Add a payment method at {provider}, then test again. Nothing else needs changing.',
                { provider: selectedProvider.name }
              )}
            </div>
            <a
              className="button small"
              href={selectedProvider.billingPageUrl}
              target="_blank"
              rel="noreferrer"
            >
              <SafeIcon name="FiExternalLink" size={14} />
              {translate('Open billing')}
            </a>
          </>
        );
      case 'model':
        return (
          <>
            <strong>{translate('This model is not available on your account')}</strong>
            <div>{translate('Choose another model above and test again.')}</div>
          </>
        );
      default:
        return (
          <>
            <strong>
              {translate('{provider} did not answer', { provider: selectedProvider.name })}
            </strong>
            <div>
              {translate('This is usually temporary. Your key is fine; try again in a minute.')}
            </div>
            <button className="button small" onClick={testConnection}>
              <SafeIcon name="FiRotateCw" size={14} />
              {translate('Try again')}
            </button>
          </>
        );
    }
  }

  return (
    <div className="ai-connect">
      <fieldset className="provider-choice">
        <legend>{translate('Which AI provider do you use?')}</legend>
        {aiProviders.map((item) => (
          <label
            key={item.id}
            className={`provider-option ${provider === item.id ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="ai-provider"
              value={item.id}
              checked={provider === item.id}
              onChange={() => {
                setProvider(item.id);
                setResult(null);
              }}
            />
            {item.name}
          </label>
        ))}
      </fieldset>
      {!compact && (
        <ol className="ai-step-cards">
          <li className="card">
            <span className="amy-avatar">
              <SafeIcon name="FiLogIn" />
            </span>
            <div>
              <strong>
                {translate('Sign in to {provider}', { provider: selectedProvider.name })}
              </strong>
              <p className="muted">
                {translate(
                  'Use the account your business pays from. No account yet? You can create one on the same page.'
                )}
              </p>
              <a
                className="button small"
                href={selectedProvider.apiKeysPageUrl}
                target="_blank"
                rel="noreferrer"
              >
                <SafeIcon name="FiExternalLink" size={14} />
                {translate('Open {provider}', { provider: selectedProvider.name })}
              </a>
            </div>
          </li>
          <li className="card">
            <span className="amy-avatar">
              <SafeIcon name="FiCreditCard" />
            </span>
            <div>
              <strong>{translate('Add a payment method')}</strong>
              <p className="muted">
                {translate(
                  '{provider} bills you directly for AI use. AMY never sees your card details.',
                  {
                    provider: selectedProvider.name,
                  }
                )}
              </p>
              <a
                className="button small"
                href={selectedProvider.billingPageUrl}
                target="_blank"
                rel="noreferrer"
              >
                <SafeIcon name="FiExternalLink" size={14} />
                {translate('Open billing')}
              </a>
            </div>
          </li>
          <li className="card">
            <span className="amy-avatar">
              <SafeIcon name="FiKey" />
            </span>
            <div>
              <strong>{translate('Create a key named "AMY"')}</strong>
              <p className="muted">
                {translate(
                  'Choose "Create key", type AMY as the name, then copy the key. It is shown only once.'
                )}
              </p>
              <div className="ai-illustration" aria-label={translate('Illustration of a key page')}>
                <span className="ai-ill-tag">{translate('Illustration')}</span>
                <div className="ai-ill-row">
                  <span>{translate('Name')}</span>
                  <span className="ai-ill-input">AMY</span>
                </div>
                <div className="ai-ill-row">
                  <span>{translate('Key')}</span>
                  <span className="ai-ill-input">{selectedProvider.prefix}••••••••••••</span>
                  <span className="ai-ill-btn">{translate('Copy')}</span>
                </div>
              </div>
            </div>
          </li>
          <li className="card">
            <span className="amy-avatar">
              <SafeIcon name="FiClipboard" />
            </span>
            <div>
              <strong>{translate('Paste it below and test')}</strong>
              <p className="muted">
                {translate(
                  'AMY checks the key straight away and tells you what to do if something is missing.'
                )}
              </p>
            </div>
          </li>
        </ol>
      )}
      {!compact && (
        <div className="notice-banner ai-cost-example">
          <SafeIcon name="FiInfo" />
          <span>
            <strong>{translate('What it might cost')}</strong> ·{' '}
            {translate(
              'Estimate for 30 posts a month: about 90 AI requests (a draft and two revisions each) of around 2,000 tokens, so roughly 180,000 tokens. On a mid-size model that is usually under US$3 a month. Images and video are not included. Prices checked {date}; your provider’s bill shows the real amount.',
              { date: pricesCheckedDate }
            )}
          </span>
        </div>
      )}
      <label className="wizard-field">
        {translate('Model')}
        <select
          className="select-field"
          value={model}
          onChange={(event) => {
            setModel(event.target.value);
            setResult(null);
          }}
        >
          {exampleModels.map((modelName) => (
            <option key={modelName} value={modelName}>
              {translate(modelName)}
            </option>
          ))}
        </select>
      </label>
      <label className="wizard-field" htmlFor="ai-key">
        {translate('API key')}
        <span className="ai-key-row">
          <input
            id="ai-key"
            className="text-field"
            type="password"
            autoComplete="off"
            value={key}
            onChange={(event) => {
              setKey(event.target.value);
              setResult(null);
            }}
            aria-invalid={result === 'empty'}
            placeholder={translate('Paste the key here')}
            disabled={readOnly}
          />
          <button
            type="button"
            className="button small"
            onClick={() => {
              setKey(`${selectedProvider.prefix}demo-0000-AMY1`);
              setResult(null);
            }}
            disabled={readOnly}
          >
            {translate('Use a demo key')}
          </button>
        </span>
        <span className="muted small-print">
          {translate(
            'Demo: do not paste a real key. Nothing leaves this browser. A real setup checks the key with {provider}.',
            { provider: selectedProvider.name }
          )}
        </span>
      </label>
      <fieldset className="ai-scenarios">
        <legend>{translate('Demo outcome of the test')}</legend>
        <div className="filter-row">
          {demoTestOutcomes.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`channel-chip ${scenario === id ? 'selected' : ''}`}
              aria-pressed={scenario === id}
              onClick={() => {
                setScenario(id);
                setResult(null);
              }}
            >
              {translate(label)}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="button-row">
        <button className="button primary" onClick={testConnection} disabled={readOnly || busy}>
          <SafeIcon name={busy ? 'FiLoader' : 'FiZap'} />
          {translate(busy ? 'Testing…' : 'Test connection')}
        </button>
      </div>
      {result && (
        <div
          className={`notice-banner ai-result ${result === 'success' ? 'ok' : 'warn'}`}
          role={result === 'success' ? 'status' : 'alert'}
        >
          <SafeIcon name={result === 'success' ? 'FiCheckCircle' : 'FiAlertTriangle'} />
          <div>{testResultMessage(result)}</div>
        </div>
      )}
    </div>
  );
}

export function HelperInvitePanel({ onGuest }: { onGuest: (id: string) => void }) {
  const { translate, language, readOnlyProps, readOnly } = useApp();
  const { route, actor, notify } = useUi();
  const { commands } = useDemo();
  const { helpers, workspace } = useWorkspaceData(route.workspaceId);
  const [inviteFormOpen, setInviteFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const pending = helpers.find((helper) => helper.status === 'Pending');
  const lastCompleted = [...helpers].reverse().find((helper) => helper.status === 'Completed');
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSendInvitation = name.trim().length > 1 && isEmailValid;

  if (pending)
    return (
      <div className="notice-banner helper-status">
        <SafeIcon name="FiClock" />
        <div style={{ flex: 1 }}>
          <strong>{translate('Waiting for {name} to connect AI', { name: pending.name })}</strong>
          <div className="muted small-print">
            {translate(
              'Invitation sent to {email} at {when}. You can carry on with the other steps.',
              {
                email: pending.email,
                when: stampLabel(pending.sentAt, language),
              }
            )}
          </div>
          <div className="button-row">
            <button
              className="button small"
              onClick={() => {
                commands.resendHelper(pending.id);
                notify(
                  translate('Invitation sent again to {email} (simulated)', {
                    email: pending.email,
                  })
                );
              }}
              {...readOnlyProps}
            >
              {translate('Resend')}
            </button>
            <button
              className="button small ghost"
              onClick={() => {
                commands.withdrawHelper(pending.id);
                notify(translate('Invitation withdrawn'));
              }}
              {...readOnlyProps}
            >
              {translate('Withdraw')}
            </button>
            <button
              className="button small ghost"
              title={translate('Demo only')}
              onClick={() => onGuest(pending.id)}
            >
              {translate('Open their link (demo)')}
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="helper-invite">
      {lastCompleted && (
        <p className="muted small-print">
          <SafeIcon name="FiCheckCircle" size={13} />{' '}
          {translate('{name} completed the AI connection at {when}.', {
            name: lastCompleted.name,
            when: stampLabel(lastCompleted.completedAt ?? '', language),
          })}
        </p>
      )}
      {!inviteFormOpen ? (
        <button className="button" onClick={() => setInviteFormOpen(true)} {...readOnlyProps}>
          <SafeIcon name="FiUserPlus" />
          {translate('Ask someone to do this for me')}
        </button>
      ) : (
        <div className="panel inset">
          <h3>{translate('Invite a helper to connect AI')}</h3>
          <div className="business-grid">
            <label className="wizard-field">
              {translate('Name')}
              <input
                className="text-field"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="wizard-field">
              {translate('Email')}
              <input
                className="text-field"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-invalid={!!email && !canSendInvitation}
              />
            </label>
          </div>
          <p className="small-print">
            <strong>{translate('What they can do')}:</strong>{' '}
            {translate(
              'Open the AI step for {ws} and connect a key. Nothing else: no posts, people or other settings.',
              { ws: workspace.name }
            )}
          </p>
          <div className="invite-preview" aria-label={translate('Invitation preview')}>
            <div className="muted small-print">{translate('Preview')}</div>
            <strong>
              {translate('{owner} asked you to connect AI for {ws}', {
                owner: actor.name,
                ws: workspace.name,
              })}
            </strong>
            <p>
              {translate(
                'Hi {name}, {owner} uses AMY to prepare social media posts. Please connect their AI account: the link opens one short step with instructions. You will not see their posts or settings.',
                { name: name.trim() || '…', owner: actor.name }
              )}
            </p>
            <span className="button small primary" aria-hidden="true">
              {translate('Connect AI for {ws}', { ws: workspace.name })}
            </span>
          </div>
          <div className="button-row">
            <button
              className="button primary"
              disabled={readOnly || !canSendInvitation}
              onClick={() => {
                commands.inviteHelper(workspace.id, name.trim(), email.trim(), actor);
                notify(
                  translate('Invitation sent to {email} (simulated)', { email: email.trim() })
                );
                setInviteFormOpen(false);
                setName('');
                setEmail('');
              }}
            >
              <SafeIcon name="FiSend" />
              {translate('Send invitation')}
            </button>
            <button className="button" onClick={() => setInviteFormOpen(false)}>
              {translate('Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function HelperGuestScreen({
  helperId,
  onClose,
}: {
  helperId: string;
  onClose: () => void;
}) {
  const { translate } = useApp();
  const { route } = useUi();
  const { commands } = useDemo();
  const { helpers, workspace } = useWorkspaceData(route.workspaceId);
  const helper = helpers.find((candidate) => candidate.id === helperId);
  const [finished, setFinished] = useState(false);
  if (!helper) return null;
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="panel modal-card guest-screen"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-title"
      >
        <div className="panel-header">
          <span className="tag">{translate('Helper view · demo')}</span>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        {finished ? (
          <div className="guest-done">
            <SafeIcon name="FiCheckCircle" size={32} />
            <h2 id="guest-title">{translate('Thank you, {name}', { name: helper.name })}</h2>
            <p>
              {translate(
                'AI is connected for {ws}. {owner} has been told. You can close this page.',
                {
                  ws: workspace.name,
                  owner: helper.by,
                }
              )}
            </p>
            <button className="button primary" onClick={onClose}>
              {translate('Back to the owner’s view')}
            </button>
          </div>
        ) : (
          <>
            <h2 id="guest-title">{translate('Connect AI for {ws}', { ws: workspace.name })}</h2>
            <p className="muted">
              {translate(
                '{owner} asked you to do this one step. You will not see their posts, people or settings.',
                { owner: helper.by }
              )}
            </p>
            <AiConnectForm
              connectedByName={helper.name}
              onConnected={() => {
                commands.completeHelper(helper.id);
                setFinished(true);
              }}
            />
          </>
        )}
      </section>
    </div>
  );
}
